// src/hooks/useUserJugs.jsx - VERSIÓN SIMPLIFICADA CON VISTA ACTUALIZADA
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import supabase from "../SupaCredentials";
import { useSupaContext } from "../contexts/SupaContext";
import toast from "react-hot-toast";

export const useUserJugs = () => {
  const { user } = useSupaContext();

  // Estados principales
  const [jugsData, setJugsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Estados calculados
  const [groupedByAddress, setGroupedByAddress] = useState({});
  const [totalJugs, setTotalJugs] = useState(0);
  const [alertsCount, setAlertsCount] = useState({
    total: 0,
    high: 0,
    medium: 0,
    low: 0,
  });

  // Referencias estables
  const userId = user?.id;
  const stableUserId = useRef(userId);
  const lastDataHash = useRef("");

  useEffect(() => {
    stableUserId.current = userId;
  }, [userId]);

  // Función simplificada para obtener datos (la vista ya hace todo el trabajo)
  const fetchUserJugs = useCallback(async () => {
    const currentUserId = stableUserId.current;

    if (!currentUserId) {
      setJugsData([]);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);

      // Query simple - la vista ya tiene todo calculado
      const { data, error: fetchError } = await supabase
        .from("user_jugs_dashboard")
        .select("*")
        .eq("user_id", currentUserId);

      if (fetchError) throw fetchError;

      // La vista ya viene ordenada y con todos los cálculos
      const processedData = data || [];

      // Verificar si los datos realmente cambiaron
      const dataHash = JSON.stringify(processedData);
      if (lastDataHash.current !== dataHash) {
        lastDataHash.current = dataHash;
        setJugsData(processedData);
      }
    } catch (err) {
      console.error("Error fetching user jugs:", err);
      setError(err.message);
      toast.error("Error al cargar tus botellones");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Función para refrescar datos
  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    await fetchUserJugs();
  }, [fetchUserJugs]);

  // Función simplificada para verificar elegibilidad - SOLO ESTOS ESTADOS SON SELECCIONABLES
  const canRequestRefill = useCallback((jug) => {
    // Solo estos estados son seleccionables para recambio
    const selectableStatuses = ['delivered', 'to_be_refilled', 'overdue_for_return'];
    return selectableStatuses.includes(jug.order_detail_status);
  }, []);

  // Procesar datos cuando cambien (simplificado)
  useEffect(() => {
    if (!jugsData.length) {
      setGroupedByAddress({});
      setTotalJugs(0);
      setAlertsCount({ total: 0, high: 0, medium: 0, low: 0 });
      return;
    }

    // Agrupar por dirección
    const grouped = jugsData.reduce((acc, jug) => {
      const addressKey = jug.address || "Sin dirección";
      if (!acc[addressKey]) {
        acc[addressKey] = {
          address: jug.address,
          city: jug.city,
          details: jug.details,
          latitude: jug.latitude,
          longitude: jug.longitude,
          delivery_address_id: jug.delivery_address_id,
          jugs: [],
        };
      }
      acc[addressKey].jugs.push(jug);
      return acc;
    }, {});

    setGroupedByAddress(grouped);
    setTotalJugs(jugsData.length);

    // Calcular alertas por prioridad (usando campos de la vista)
    const alerts = jugsData.reduce(
      (acc, jug) => {
        if (jug.alert_priority) {
          acc.total++;
          acc[jug.alert_priority] = (acc[jug.alert_priority] || 0) + 1;
        }
        return acc;
      },
      { total: 0, high: 0, medium: 0, low: 0 }
    );

    setAlertsCount(alerts);
  }, [jugsData]);

  // Cargar datos al montar y cuando cambie el usuario
  useEffect(() => {
    fetchUserJugs();
  }, [fetchUserJugs]);

  // Función para solicitar recambio
  const requestRefill = useCallback(
    async (jugId, addressId) => {
      if (!user?.id || !jugId || !addressId) {
        toast.error("Información incompleta para solicitar recambio");
        return false;
      }

      try {
        // Llamar a la función de Supabase para crear orden de recambio
        const { data, error } = await supabase.rpc("create_refill_order", {
          user_id_param: user.id,
          address_id_param: addressId,
          jug_id_param: jugId,
          scheduled_date_param: null, // Se programa automáticamente
        });

        if (error) throw error;

        toast.success("¡Recambio solicitado exitosamente!", {
          duration: 4000,
          position: "top-center",
          style: {
            background: "#10B981",
            color: "#fff",
          },
        });

        // Refrescar datos después de crear el recambio
        await refreshData();

        return true;
      } catch (err) {
        console.error("Error requesting refill:", err);
        toast.error(`Error al solicitar recambio: ${err.message}`);
        return false;
      }
    },
    [user?.id, refreshData]
  );

  // Función para procesar pago
  const processPayment = useCallback(
    async (jugId, amount) => {
      if (!user?.id || !jugId) {
        toast.error("Información incompleta para procesar pago");
        return false;
      }

      try {
        // Aquí implementarías la integración con tu pasarela de pago
        // Por ahora, simular redirección
        toast.success(`Redirigiendo a pasarela de pago por ${new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency: 'COP',
          minimumFractionDigits: 0,
        }).format(amount)}...`, {
          duration: 3000,
          position: "top-center",
        });

        // Simular actualización del estado después del pago
        setTimeout(async () => {
          await refreshData();
        }, 2000);

        return true;
      } catch (err) {
        console.error("Error processing payment:", err);
        toast.error(`Error al procesar pago: ${err.message}`);
        return false;
      }
    },
    [user?.id, refreshData]
  );

  // Funciones de utilidad simplificadas (usando campos de la vista)
  const getJugStatusLabel = useCallback((jug) => {
    // La vista ya calcula alert_message, pero mantenemos esta función para compatibilidad
    if (jug.order_detail_status === "delivered" && jug.alert_type) {
      const alertLabels = {
        'mandatory_return': 'Retorno necesario',
        'return_required': 'Para retornar', 
        'expiration_warning': 'Recambio sugerido'
      };
      return alertLabels[jug.alert_type] || 'Entregado';
    }

    const statusLabels = {
      recently_ordered: "Recién pedido",
      ready_for_production: "En cola de producción",
      in_washing: "En cola de producción", 
      in_filling: "En cola de producción",
      ready_for_delivery: "En camino",
      in_transit: "En camino",
      delivered: "Entregado",
      to_be_refilled: "Para recambio",
      to_be_returned: "Para retornar",
      overdue_for_return: "Vencido",
    };

    return statusLabels[jug.order_detail_status] || "Desconocido";
  }, []);

  const getStatusBadgeColor = useCallback((jug) => {
    // Usar alert_priority de la vista
    if (jug.alert_priority === "high") return "red";
    if (jug.alert_priority === "medium") return "orange"; 
    if (jug.alert_priority === "low") return "yellow";

    const colorMap = {
      delivered: "green",
      in_transit: "blue",
      ready_for_delivery: "blue", 
      recently_ordered: "gray",
      ready_for_production: "blue",
      in_washing: "blue",
      in_filling: "blue",
    };

    return colorMap[jug.order_detail_status] || "gray";
  }, []);

  // Estadísticas mejoradas usando los campos de la vista
  const stats = useMemo(() => ({
    total: totalJugs,
    delivered: jugsData.filter(j => j.order_detail_status === 'delivered').length,
    inProduction: jugsData.filter(j => ['recently_ordered', 'ready_for_production', 'in_washing', 'in_filling', 'ready_for_delivery', 'in_transit'].includes(j.order_detail_status)).length,
    needsAttention: jugsData.filter(j => j.needs_attention === true).length,
    canRefill: jugsData.filter(j => canRequestRefill(j)).length,
    pendingPayment: jugsData.filter(j => j.payment_status === 'pending' || j.payment_status === 'failed').length,
    // Estadísticas por tipo de alerta
    alerts: {
      high: jugsData.filter(j => j.alert_priority === 'high').length,
      medium: jugsData.filter(j => j.alert_priority === 'medium').length, 
      low: jugsData.filter(j => j.alert_priority === 'low').length,
    }
  }), [totalJugs, jugsData, canRequestRefill]);

  // Valores computados
  const computedValues = useMemo(() => ({
    hasJugs: totalJugs > 0,
    hasAlerts: alertsCount.total > 0,
    stats,
  }), [totalJugs, alertsCount.total, stats]);

  return {
    // Datos principales
    jugsData,
    groupedByAddress,
    totalJugs,
    alertsCount,

    // Estados de carga
    isLoading,
    isRefreshing,
    error,

    // Funciones
    refreshData,
    requestRefill,
    processPayment,
    getJugStatusLabel,
    getStatusBadgeColor,
    canRequestRefill,

    // Estado computado
    ...computedValues,
  };
};